import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { Button } from '@/shared/ui/chakra/button';
import { isChunkLoadError, reloadAfterChunkError } from '@/shared/lib/chunk-reload';

/* Экран на случай, когда страница админки упала, — вместо английского «Something went wrong!»
   роутера. Не скачался файл сборки — перезагружаемся сами (см. `shared/lib/chunk-reload`), и
   сотрудник видит «Обновляем страницу…», а не ошибку. Не вышло или ошибка другая — русский
   текст и кнопка «Обновить»; текст самой ошибки мелко внизу, для снимка экрана. */
export function RouteErrorFallback({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  const chunk = isChunkLoadError(error);
  // Сначала считаем, что перезагрузимся, — иначе на кадр мелькнула бы ошибка.
  const [reloading, setReloading] = useState(chunk);

  useEffect(() => {
    if (chunk && !reloadAfterChunkError()) setReloading(false);
  }, [chunk]);

  if (reloading) {
    return (
      <Flex align="center" justify="center" gap="3" p="10" color="fg.muted">
        <Spinner size="sm" />
        <Text>{t('common.reloading')}</Text>
      </Flex>
    );
  }

  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    <Flex align="center" justify="center" p="10" data-testid="route-error">
      <Stack align="center" gap="3" textAlign="center" maxW="md">
        <Text fontSize="lg" fontWeight="medium">
          {chunk ? t('common.page_load_failed_title') : t('common.page_error_title')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {chunk ? t('common.page_load_failed_body') : t('common.page_error_body')}
        </Text>
        <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
        {message ? (
          <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
            {message}
          </Text>
        ) : null}
      </Stack>
    </Flex>
  );
}
